import React, { useCallback, useMemo } from "react";
import {
  Calendar,
  momentLocalizer,
  Event as CalendarEvent,
  View,
} from "react-big-calendar";
import moment from "moment-timezone";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "./SchedulerPage.css";

import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import EventComponent from "./components/EventComponent";
import { Visit } from "@visitproof/types";
import { useNavigate } from "react-router-dom";
import { Heading } from "../../design/heading";
import { useAccount } from "../../contexts/AccountContext";
import UpdateVisitAlert from "./components/UpdateVisitAlert";
import { Button } from "../../design/button";
import { useAuth } from "../../contexts/AuthContext";
import CreateMultipleVisitsButton from "../../components/CreateMultipleVisitsButton";
import Toolbar from "./components/Toolbar";
import { useScheduler } from "../../contexts/SchedulerContext";
import { useCreateVisitDialog } from "../../providers/CreateVisitDialogProvider";
import { useSchedulerData } from "./hooks/useSchedulerData";
import { useSchedulerActions } from "./hooks/useSchedulerActions";
import { colorForStatus } from "./utils/statusColors";
import LoadingSkeleton from "./components/LoadingSkeleton";
import ErrorView from "../../components/ErrorView";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DnDCalendar = withDragAndDrop(Calendar) as any;
const localizer = momentLocalizer(moment);

const VisitCalendar: React.FC = () => {
  const { user } = useAuth();
  const { openDialog } = useCreateVisitDialog();
  const { calendarView, setCalendarView } = useScheduler();
  const { account } = useAccount();
  const navigate = useNavigate();

  const {
    date,
    setDate,
    setRange,
    visits,
    filteredVisits,
    updatingEvent,
    setUpdatingEvent,
    loadingVisits,
    error,
    refresh,
  } = useSchedulerData();

  const {
    handleSelectSlot,
    handleRangeChange,
    handleEventChanged,
    handleNylasButtonClick,
    handleNavigate,
    handleViewChange,
    handleDoubleClickEvent,
    handleCloseUpdateAlert,
  } = useSchedulerActions({
    user,
    account,
    openDialog,
    navigate,
    setDate,
    setRange,
    setUpdatingEvent,
    calendarView,
    setCalendarView,
  });

  // Memoize calendar events to prevent unnecessary re-renders
  const calendarEvents = useMemo(() => 
    filteredVisits.map((visit) => ({
      id: visit.id,
      start: visit.startDate.toDate(),
      end: visit.endDate.toDate(),
      resource: visit,
    })), [filteredVisits]
  );

  // Memoize event prop getter
  const eventPropGetter = useCallback((event: CalendarEvent) => {
    const visit = event.resource as Visit;
    return {
      style: { 
        backgroundColor: colorForStatus(visit.status),
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '4px',
      },
    };
  }, []);

  // Early return for loading state
  if (loadingVisits && visits.length === 0) {
    return <LoadingSkeleton />;
  }

  // Early return for error state
  if (error) {
    return <ErrorView error={error} />;
  }

  return (
    <div className="scheduler-container">
      {/* Header Section */}
      <div className="scheduler-header">
        <div className="flex items-center justify-between">
          <Heading>Visit Calendar</Heading>
          <div className="flex gap-2">
            {!account?.nylasGrantId && (
              <Button 
                onClick={handleNylasButtonClick}
                color="blue"
              >
                Connect Calendar
              </Button>
            )}
            <CreateMultipleVisitsButton />
          </div>
        </div>
      </div>

      {/* Update Alert */}
      <UpdateVisitAlert
        visit={updatingEvent}
        onClose={() => {
          handleCloseUpdateAlert();
          // Re-read visits from Firestore so a dismissed drag/resize snaps the
          // event back to its saved position (nothing was persisted).
          refresh();
        }}
      />

      {/* Calendar Component */}
      <div className="calendar-wrapper">
        <DnDCalendar
          localizer={localizer}
          events={calendarEvents}
          date={date}
          view={calendarView as View}
          onView={handleViewChange}
          onNavigate={handleNavigate}
          onSelectSlot={handleSelectSlot}
          onRangeChange={handleRangeChange}
          onEventResize={handleEventChanged}
          onEventDrop={handleEventChanged}
          onDoubleClickEvent={handleDoubleClickEvent}
          selectable
          resizable
          components={{
            event: EventComponent,
            toolbar: Toolbar,
          }}
          eventPropGetter={eventPropGetter}
          style={{ height: "80vh" }}
          className="scheduler-calendar"
          popup
          step={15}
          timeslots={4}
          defaultView="month"
          messages={{
            next: "Next",
            previous: "Previous",
            today: "Today",
            month: "Month",
            week: "Week",
            day: "Day",
            agenda: "Agenda",
            date: "Date",
            time: "Time",
            event: "Event",
            noEventsInRange: "No visits in this range",
            showMore: (total: number) => `+${total} more`,
          }}
        />
      </div>
    </div>
  );
};

export default VisitCalendar;
